import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { ProjectGq } from '../../../../graphql/projects/models/ProjectGq';
import { CurrentUserGq } from '../../../../graphql/users/models/CurrentUserGq';
import { ProjectTabChoices } from '../../../../pages/ProjectPage/ProjectPage';
import VoxelTooltipShort from '../../../voxel/Tooltip/VoxelTooltipShort';
import ProjectOptionsButton from './ProjectOptionsButton';
import VoxelTableCell from '../../../voxel/Tables/VoxelTableCell';
import VoxelTableRow from '../../../voxel/Tables/VoxelTableRow';
import VoxelAvatar from '../../../voxel/VoxelAvatar';
import ProjectTableStats from './ProjectTableStats';
import VoxelText from '../../../voxel/Text/VoxelText';
import { DomainTypeGq } from '../../../../graphql/projects/models/DomainTypeGq';
import VoxelChip from '../../../voxel/Chips/VoxelChip';
import { faShareNodes } from '@fortawesome/pro-regular-svg-icons';

export interface ProjectTableRowProps {
  project: ProjectGq;
  setShowCopyProgressId?: (id: number) => void;
  copyProject?: (id: ProjectGq) => void;
  setShowTranslateProgressId?: (id: number) => void;
  translateProject?: (id: ProjectGq) => void;
  showOptions?: boolean;
  rowComponent?: 'a' | 'div';
  onClick?: (row: ProjectGq) => void;
  highlighedProjectIds?: number[];
  getHighlightedProject?: (project: ProjectGq) => void;
  user?: CurrentUserGq;
}

const ProjectTableRow: React.FC<ProjectTableRowProps> = ({
  project,
  setShowCopyProgressId,
  copyProject,
  setShowTranslateProgressId,
  translateProject,
  showOptions,
  rowComponent,
  onClick,
  highlighedProjectIds,
  user,
  getHighlightedProject,
}) => {
  const history = useHistory();

  // Handle go to project
  let url = `/${project.id}/${ProjectTabChoices.Manage}`;
  const handleGoToProject = useCallback(
    (event) => {
      event.preventDefault();
      if (!!onClick) {
        onClick(project);
        return;
      }

      // Only show progress if copy is in progress
      if (project.copyInProgress) {
        setShowCopyProgressId(project.id);
        return;
      }
      if (project.translateInProgress) {
        setShowTranslateProgressId(project.id);
        return;
      }
      history.push(url);
    },
    [project.id, project.copyInProgress, onClick]
  );

  // Maintain a local archived state to make it feel snappier
  const [archived, setArchived] = useState(!!project.archivedById);
  useEffect(() => setArchived(!!project.archivedTime), [project.archivedTime]);

  const projectDomain = project.domains?.find((d) => d?.type === DomainTypeGq.PROJECT)
    ? project.domains?.find((d) => d?.type === DomainTypeGq.PROJECT)
    : null;
  const selected =
    highlighedProjectIds?.includes(project.id) ||
    (getHighlightedProject && getHighlightedProject(project));

  const langIsMixed = project.serpLanguageId !== project.defaultLanguageId;
  const locIsMixed = project.serpLocationId !== project.defaultLocationId;

  return (
    <VoxelTableRow
      key={project.id}
      button
      selected={!!selected}
      onClick={handleGoToProject}
      className={`${archived ? 'bg-voxel-gray-50' : ''}`}
    >
      <VoxelTableCell size="lg" className={`${archived ? 'opacity-50' : ''} flex flex-col gap-1`}>
        <div className='flex flex-row gap-1 items-center flex-nowrap'>
          {project.exampleProject && (
            <VoxelChip title="Demo" color="neutral" size='sm' textTransform='capitalize' />
          )}
          {project.exampleProjectSource && (
            <VoxelChip title="Example source" color="neutral" size='sm' />
          )}
          <div className="max-w-xs truncate">{project.name}</div>
        </div>
        {projectDomain !== null && (
          <div className="font-sans text-xs text-voxel-gray-500/90 font-medium truncate max-w-xs">
            {projectDomain.hostName}
          </div>
        )}
      </VoxelTableCell>
      <VoxelTableCell size="lg" className={`whitespace-nowrap ${archived ? 'opacity-50' : ''}`}>
        {project.defaultLanguageId
          ? project.defaultLanguageName
          : langIsMixed
            ? 'All languages (' + project.serpLanguageName + ')'
            : 'All languages'}
      </VoxelTableCell>

      <VoxelTableCell size="lg" className={`whitespace-nowrap ${archived ? 'opacity-50' : ''}`}>
        {project.defaultLocationId
          ? project.defaultLocationName
          : locIsMixed
            ? 'Global ' + '(' + project.serpLocationName + ')'
            : 'Global'}
      </VoxelTableCell>

      <VoxelTableCell size="lg" className={`whitespace-nowrap ${archived ? 'opacity-50' : ''}`}>
        <VoxelTooltipShort
          message={
            !!project.createdTime ? moment.utc(project.createdTime).local().format('LLLL') : '-'
          }
        >
          <span>
            {!!project.createdTime
              ? moment.utc(project.createdTime).local().format('MMM Do, YY')
              : '-'}
          </span>
        </VoxelTooltipShort>

      </VoxelTableCell>
      <VoxelTableCell size="lg" align="center" className={`${archived ? 'opacity-50' : ''} `}>
        <div className='flex flex-row items-center justify-center flex-nowrap gap-1'>
          <VoxelAvatar
            tooltip={
              <>
                {project.createdBy?.givenName + ' ' + project.createdBy?.familyName}
                <br />
                {project.createdBy?.email ?? '?'}
              </>
            }
            name={project.createdBy?.email}
            size="sm"
            img={project.createdBy?.picture}
          />
          {project.createdBy?.team?.id !== user?.team?.id && (
            <VoxelAvatar name={project.createdBy?.team?.name} size="sm" tooltip={project.createdBy?.team?.name} />
          )}
        </div>
      </VoxelTableCell>
      <VoxelTableCell size="lg" align="left" className={`${archived ? 'opacity-50' : ''}`}>
        <ProjectTableStats project={project} />
      </VoxelTableCell>
      {showOptions && (
        <VoxelTableCell
          size="sm"
          align="right"
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
          }}
        >
          <div className="flex flex-row justify-end items-center gap-1 flex-nowrap pr-2">
            <VoxelTooltipShort message={`This project is shared with other team(s)`}>
              <span className="flex flex-row items-center gap-1.5 justify-end">
                <VoxelText
                  variant={'form-caption'}
                  className={`${!project.grantedTeamAccess?.length ? 'invisible' : 'font-semibold'
                    }`}
                  color="slight"
                >
                  {project.grantedTeamAccess?.length}
                </VoxelText>
                <FontAwesomeIcon
                  icon={faShareNodes}
                  className={`${project.grantedTeamAccess?.length
                    ? ' text-voxel-gray-600'
                    : 'text-voxel-gray-200'
                    }`}
                />
              </span>
            </VoxelTooltipShort>
            <ProjectOptionsButton
              user={user}
              project={project}
              archived={archived}
              setArchived={setArchived}
              setShowCopyProgressId={setShowCopyProgressId}
              copyProject={copyProject}
              translateProject={translateProject}
              setShowTranslateProgressId={setShowTranslateProgressId}
            />
          </div>
        </VoxelTableCell>
      )
      }
    </VoxelTableRow >
  );
};

export default ProjectTableRow;
